<template>
  <LegacyWrap>
    <div class="main-space">
      <ExpansionPanels
        v-faq-view-tracker
        class="frequently-asked-questions"
        item-scope
        item-type="https://schema.org/FAQPage"
      >
        <template #title>
          <h2 class="frequently-asked-questions__title">{{ t('title') }}</h2>
        </template>

        <template #default>
          <template v-for="(city, i) in tags" :key="i">
            <Item :open="openSection[i]" @toggled="toggle(i, $event)">
              <template #title>{{ city.name }}</template>
              <template #default>
                <TagsList :list="city.tags" :city="toTagCity(city)" @use-tag="clickOnTag" />
              </template>
            </Item>
          </template>
        </template>
      </ExpansionPanels>
    </div>
  </LegacyWrap>
</template>

<script lang="ts" setup>
import { useExpansionPanelsController, ExpansionPanels, Item } from '@experience/core-expansion-panels-ui';
import { useI18n } from '@experience/i18n';
import { ref, onMounted } from 'vue';

import ru from '@/features/categoryContainer/i18n/ru';
import TagsList from '@/features/categoryContainer/tags/TagsList.vue';
import { getCityTagsList } from '@/shared/api/generated/api';
import { recursiveToCamel } from '@/shared/helpers/toCamel';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useAnalytics } from '@/shared/lib/analytics';
import { type CityTags, type TagTypeWithCity } from '@/shared/models/tag';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { useViewTracker, trackEventImmediately } = useAnalytics();

const tags = ref<CityTags[]>([]);

const { openSection, toggle } = useExpansionPanelsController();

const vFaqViewTracker = useViewTracker('main:categories.view');

onMounted(() => fetchData());

const fetchData = async () => {
  const { data } = await getCityTagsList();

  tags.value = recursiveToCamel(data ?? []);
};

const toTagCity = (city: CityTags) => ({ cityName: city.name, citySlug: city.slug });

const clickOnTag = (el: TagTypeWithCity) => {
  void trackEventImmediately('main:categories:category.click', {
    subject_geo_id: el.citytagId,
    subject_geo_name: el.cityName,
    subject_geo_type: 'city',
    location: 'main',
    tag_id: el.citytagId,
  });
};
</script>

<style lang="scss" scoped>
.frequently-asked-questions__title {
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  line-height: inherit;
}
</style>
